import React, { useContext, useEffect, useState } from 'react';
import { Modal, ModalHeader, ModalBody, Table, FormGroup, Label, Input, Button, UncontrolledTooltip } from 'reactstrap';
import Select from 'react-select';
import { MdDeleteOutline, MdEdit } from 'react-icons/md';
import { toast } from 'react-toastify';
import { SrxApi } from '../../../../api/SrxApi';
import { ButtonLoader, DataFetchLoader } from '../../../../components/Loader.js';
import { confirm } from '../../../../utility-components/ConfirmModal';
import {
    CUSTOM_DATA_TYPES,
    DATA_TYPES,
    DEFAULT_INTERNAL_NAMES,
    INTERNAL_NAME_PATTERN,
    PARAMETER_TYPES,
    getSqlError,
    getSqlParameterNames,
} from '../pharmacyReportsHelpers.js';
import SharedParametersModal from './SharedParametersModal.js';
import { UserContext } from '../../../../contexts/UserContext';
import { hasRolePermission } from '../../../../utility-services/rolePermissions.js';

const compactSelectStyles = {
    control: (base) => ({ ...base, minHeight: '31px', fontSize: '0.8125rem' }),
    valueContainer: (base) => ({ ...base, padding: '0 8px', height: '29px' }),
    input: (base) => ({ ...base, margin: 0, padding: 0 }),
    indicatorsContainer: (base) => ({ ...base, height: '29px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const permissionSelectStyles = {
    control: (base) => ({ ...base, minHeight: '38px', borderRadius: '8px' }),
    valueContainer: (base) => ({ ...base, padding: '4px 8px' }),
    indicatorSeparator: () => ({ display: 'none' }),
    clearIndicator: (base) => ({ ...base, padding: '6px' }),
    dropdownIndicator: (base) => ({ ...base, padding: '6px' }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const EMPTY_FORM = { reportName: '', description: '', permissionName: [], sqlText: '', parameters: [] };

function toPermissionNameArray(value) {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (!value) return [];
    return String(value)
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean);
}

const EMPTY_PARAMETER = {
    parameterType: 'Custom',
    displayName: '',
    internalName: '',
    dataType: 'Text',
    sharedParameterId: null,
    isRequired: false,
};

const SHARED_PREFIX = 'shared:';

function toFormParameters(parameters) {
    return (parameters || []).map((parameter) => ({
        parameterType: parameter.parameterType,
        displayName: parameter.displayName || '',
        internalName: parameter.internalName || '',
        dataType: parameter.dataType || 'Text',
        sharedParameterId: parameter.sharedParameterId ?? null,
        isRequired: Boolean(parameter.isRequired),
    }));
}

function toTypeValue(parameter) {
    return parameter.parameterType === 'Shared'
        ? `${SHARED_PREFIX}${parameter.sharedParameterId}`
        : parameter.parameterType;
}

function toForm(report) {
    return {
        reportName: report.reportName || '',
        description: report.description || '',
        permissionName: toPermissionNameArray(report.permissionName),
        sqlText: report.sqlText || '',
        parameters: toFormParameters(report.parameters),
    };
}

function getFormError(form) {
    if (!form.reportName.trim()) return 'Enter a report name.';
    if (!form.sqlText.trim()) return 'Enter the SQL.';

    const sqlError = getSqlError(form.sqlText);
    if (sqlError) return sqlError;

    const index = form.parameters.findIndex(
        (parameter) =>
            !parameter.internalName.trim() || (parameter.parameterType === 'Custom' && !parameter.displayName.trim())
    );
    if (index !== -1) {
        return form.parameters[index].internalName.trim()
            ? `Enter a display name for parameter ${index + 1}.`
            : `Enter an internal name for parameter ${index + 1}.`;
    }

    const parameterNames = form.parameters.map((parameter) => parameter.internalName.trim());
    const invalidName = parameterNames.find((name) => !INTERNAL_NAME_PATTERN.test(name));
    if (invalidName) return `Internal name '${invalidName}' can only have lowercase letters, numbers and underscores.`;

    const duplicate = parameterNames.find((name, i) => parameterNames.indexOf(name) !== i);
    if (duplicate) return `Parameter '${duplicate}' is added more than once.`;

    const sqlNames = getSqlParameterNames(form.sqlText);
    const missing = sqlNames.find((name) => !parameterNames.includes(name));
    if (missing) return `The SQL uses @${missing} but the report has no parameter named '${missing}'.`;

    const unused = parameterNames.find((name) => !sqlNames.includes(name));
    return unused ? `Parameter '${unused}' isn't used in the SQL.` : null;
}

function toPayload(form) {
    return {
        reportName: form.reportName.trim(),
        description: form.description,
        permissionName: form.permissionName.length ? form.permissionName : null,
        sqlText: form.sqlText,
        parameters: form.parameters.map((parameter) => ({
            parameterType: parameter.parameterType,
            displayName: parameter.parameterType === 'Custom' ? parameter.displayName : null,
            internalName: parameter.internalName.trim(),
            dataType: parameter.parameterType === 'Custom' ? parameter.dataType : null,
            sharedParameterId: parameter.parameterType === 'Shared' ? parameter.sharedParameterId : null,
            isRequired: parameter.isRequired,
        })),
    };
}

function ReportSetupModal({ isOpen, onClose, onReportsChanged }) {
    const { roles } = useContext(UserContext);
    const canEdit = hasRolePermission(roles, 'canEditManageReports');
    const canDelete = hasRolePermission(roles, 'canDeleteManageReports');
    const [reports, setReports] = useState([]);
    const [permissionOptions, setPermissionOptions] = useState([]);
    const [sharedParameters, setSharedParameters] = useState([]);
    const [isSharedParametersOpen, setIsSharedParametersOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [originalForm, setOriginalForm] = useState(EMPTY_FORM);
    const [isSaving, setIsSaving] = useState(false);

    const isEditing = editingId != null;

    const loadReports = () => {
        setIsLoading(true);
        SrxApi.get('/internalreports/reports')
            .then((res) =>
                setReports(
                    (Array.isArray(res?.data) ? res.data : []).sort((a, b) =>
                        (a.reportName || '').localeCompare(b.reportName || '')
                    )
                )
            )
            .catch((err) => {
                toast.error(err?.response?.data?.message || 'Error retrieving reports: ' + err.message);
                setReports([]);
            })
            .finally(() => setIsLoading(false));
    };

    const loadPermissions = () => {
        SrxApi.get('/permissions')
            .then((res) => {
                const groups = res?.data?.permissionGroups || res?.data || [];
                const names = (Array.isArray(groups) ? groups : [])
                    .flatMap((group) => group.permissions || [])
                    .map((permission) => permission.permissionName)
                    .filter(Boolean);
                setPermissionOptions([...new Set(names)].sort());
            })
            .catch((err) =>
                toast.error(err?.response?.data?.message || 'Error retrieving permissions: ' + err.message)
            );
    };

    const loadSharedParameters = () => {
        SrxApi.get('/internalreports/sharedparameters')
            .then((res) => setSharedParameters(Array.isArray(res?.data) ? res.data : []))
            .catch((err) =>
                toast.error(err?.response?.data?.message || 'Error retrieving shared parameters: ' + err.message)
            );
    };

    useEffect(() => {
        if (isOpen) {
            loadReports();
            loadPermissions();
            loadSharedParameters();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!sharedParameters.length) return;
        setForm((prev) => {
            let changed = false;
            const parameters = prev.parameters.map((parameter) => {
                if (parameter.parameterType !== 'Shared' || parameter.sharedParameterId == null) return parameter;
                const shared = sharedParameters.find(
                    (item) => item.internalReportSharedParameterId === parameter.sharedParameterId
                );
                if (!shared) return parameter;
                if (
                    parameter.displayName === shared.name &&
                    parameter.internalName === shared.internalName &&
                    parameter.dataType === shared.dataType
                ) {
                    return parameter;
                }
                changed = true;
                return {
                    ...parameter,
                    displayName: shared.name,
                    internalName: shared.internalName,
                    dataType: shared.dataType,
                };
            });
            return changed ? { ...prev, parameters } : prev;
        });
    }, [sharedParameters]);

    const closeForm = () => {
        setIsFormOpen(false);
        setEditingId(null);
        setForm(EMPTY_FORM);
    };

    const openAddForm = () => {
        setEditingId(null);
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
        setIsFormOpen(true);
    };

    const openEditForm = (report) => {
        const values = toForm(report);
        setEditingId(report.internalReportId);
        setForm(values);
        setOriginalForm(values);
        setIsFormOpen(true);
    };

    const handleFieldChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleParameterChange = (index, field, value) => {
        setForm((prev) => ({
            ...prev,
            parameters: prev.parameters.map((parameter, i) => {
                if (i !== index) return parameter;
                if (field !== 'parameterType') return { ...parameter, [field]: value };
                if (value.startsWith(SHARED_PREFIX)) {
                    const shared = sharedParameters.find(
                        (item) => String(item.internalReportSharedParameterId) === value.slice(SHARED_PREFIX.length)
                    );
                    return {
                        ...parameter,
                        parameterType: 'Shared',
                        sharedParameterId: shared.internalReportSharedParameterId,
                        displayName: shared.name,
                        internalName: shared.internalName,
                        dataType: shared.dataType,
                    };
                }
                const internalName =
                    value === 'Custom'
                        ? parameter.internalName
                        : DEFAULT_INTERNAL_NAMES[value] || parameter.internalName;
                const dataType = CUSTOM_DATA_TYPES.includes(parameter.dataType) ? parameter.dataType : 'Text';
                return { ...parameter, parameterType: value, internalName, dataType, sharedParameterId: null };
            }),
        }));
    };

    const addParameter = () => {
        setForm((prev) => ({ ...prev, parameters: [...prev.parameters, EMPTY_PARAMETER] }));
    };

    const removeParameter = (index) => {
        setForm((prev) => ({ ...prev, parameters: prev.parameters.filter((parameter, i) => i !== index) }));
    };

    const handleSave = () => {
        const formError = getFormError(form);
        if (formError) {
            toast.error(formError);
            return;
        }

        setIsSaving(true);
        const payload = toPayload(form);
        const request = isEditing
            ? SrxApi.post(`/internalreports/reports/${editingId}`, payload)
            : SrxApi.post('/internalreports/reports', payload);

        request
            .then(() => {
                toast.success(isEditing ? 'Report updated.' : 'Report added.');
                closeForm();
                loadReports();
                if (onReportsChanged) onReportsChanged();
            })
            .catch((err) => toast.error(err?.response?.data?.message || 'Error saving report: ' + err.message))
            .finally(() => setIsSaving(false));
    };

    const handleDelete = async (report) => {
        if (!(await confirm(`Delete <i>${report.reportName}</i>?`, 'Delete Report', 'Delete', 'Cancel'))) return;

        SrxApi.delete(`/internalreports/reports/${report.internalReportId}`)
            .then(() => {
                toast.success('Report deleted.');
                if (editingId === report.internalReportId) {
                    closeForm();
                }
                loadReports();
                if (onReportsChanged) onReportsChanged();
            })
            .catch((err) => toast.error(err?.response?.data?.message || 'Error deleting report: ' + err.message));
    };

    const closeModal = () => {
        closeForm();
        onClose();
    };

    const handleSharedParametersChanged = () => {
        loadSharedParameters();
        loadReports();
        if (onReportsChanged) onReportsChanged();
    };

    const isUnchanged = isEditing && JSON.stringify(form) === JSON.stringify(originalForm);
    const formError = isFormOpen ? getFormError(form) : null;

    return (
        <Modal isOpen={isOpen} toggle={closeModal} size="xl" centered>
            <ModalHeader toggle={closeModal}>Report Setup</ModalHeader>
            <ModalBody>
                {isLoading ? (
                    <DataFetchLoader name="Loading reports..." />
                ) : (
                    <>
                        <div className="d-flex justify-content-end mb-3" style={{ gap: '12px' }}>
                            <Button
                                color="secondary"
                                outline
                                size="sm"
                                style={{ cursor: 'pointer' }}
                                onClick={() => setIsSharedParametersOpen(true)}
                            >
                                Shared Parameters
                            </Button>
                            {!isFormOpen && (
                                <Button color="primary" size="sm" style={{ cursor: 'pointer' }} onClick={openAddForm}>
                                    Add Report
                                </Button>
                            )}
                        </div>

                        {isFormOpen && (
                            <div className="border rounded p-3 mb-3">
                                <div className="d-flex flex-wrap" style={{ gap: '16px' }}>
                                    <FormGroup style={{ flex: '1 1 280px' }}>
                                        <Label>Report Name</Label>
                                        <Input
                                            maxLength={100}
                                            value={form.reportName}
                                            onChange={(e) => handleFieldChange('reportName', e.target.value)}
                                        />
                                    </FormGroup>
                                    <FormGroup style={{ flex: '1 1 280px' }}>
                                        <Label>Permission</Label>
                                        <Select
                                            placeholder="Optional: leave empty for all users"
                                            isClearable
                                            isMulti
                                            controlShouldRenderValue={false}
                                            styles={permissionSelectStyles}
                                            menuPortalTarget={document.body}
                                            value={form.permissionName.map((name) => ({ value: name, label: name }))}
                                            onChange={(options) =>
                                                handleFieldChange(
                                                    'permissionName',
                                                    options ? options.map((option) => option.value) : []
                                                )
                                            }
                                            options={permissionOptions.map((name) => ({
                                                value: name,
                                                label: name,
                                            }))}
                                            noOptionsMessage={() => 'No matching permission'}
                                        />
                                        {form.permissionName.length > 0 && (
                                            <div className="d-flex flex-wrap align-items-center" style={{ gap: '6px', marginTop: '8px' }}>
                                                {form.permissionName.slice(0, 2).map((name) => (
                                                    <span
                                                        key={name}
                                                        style={{
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            backgroundColor: '#EEF2FF',
                                                            color: '#3730A3',
                                                            borderRadius: '9999px',
                                                            fontSize: '0.8125rem',
                                                            padding: '4px 6px 4px 10px',
                                                        }}
                                                    >
                                                        {name}
                                                        <span
                                                            role="button"
                                                            aria-label={`Remove ${name}`}
                                                            onClick={() =>
                                                                handleFieldChange(
                                                                    'permissionName',
                                                                    form.permissionName.filter((n) => n !== name)
                                                                )
                                                            }
                                                            style={{
                                                                marginLeft: '6px',
                                                                cursor: 'pointer',
                                                                borderRadius: '9999px',
                                                                padding: '0 6px',
                                                                color: '#4338CA',
                                                            }}
                                                        >
                                                            ×
                                                        </span>
                                                    </span>
                                                ))}
                                                {form.permissionName.length > 2 && (
                                                    <>
                                                        <span
                                                            id="permission-more-badge"
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                backgroundColor: '#F3F4F6',
                                                                color: '#6c757d',
                                                                border: '1px solid #E5E7EB',
                                                                borderRadius: '9999px',
                                                                fontSize: '0.8125rem',
                                                                padding: '4px 10px',
                                                                cursor: 'default',
                                                            }}
                                                        >
                                                            +{form.permissionName.length - 2} more
                                                        </span>
                                                        <UncontrolledTooltip target="permission-more-badge" placement="top">
                                                            {form.permissionName.slice(2).join(', ')}
                                                        </UncontrolledTooltip>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </FormGroup>
                                    <FormGroup style={{ flex: '1 1 280px' }}>
                                        <Label>Description</Label>
                                        <Input
                                            type="textarea"
                                            rows={1}
                                            maxLength={500}
                                            style={{ resize: 'none' }}
                                            value={form.description}
                                            onChange={(e) => handleFieldChange('description', e.target.value)}
                                        />
                                    </FormGroup>
                                </div>
                                <FormGroup>
                                    <Label>SQL</Label>
                                    <Input
                                        type="textarea"
                                        rows={4}
                                        style={{
                                            fontFamily: 'monospace',
                                            fontSize: '13px',
                                            resize: 'none',
                                            maxHeight: '200px',
                                            overflowY: 'auto',
                                        }}
                                        placeholder="SELECT * FROM fn_get_facility_logins(@date_from, @date_to, @user_name, @facilities)"
                                        value={form.sqlText}
                                        onChange={(e) => handleFieldChange('sqlText', e.target.value)}
                                    />
                                </FormGroup>

                                <Label>Parameters</Label>
                                {(() => {
                                    const typeOptions = [
                                        ...PARAMETER_TYPES.map((type) => ({ value: type, label: type })),
                                        ...(sharedParameters.length > 0
                                            ? [
                                                  {
                                                      label: 'Shared Parameters',
                                                      options: sharedParameters.map((shared) => ({
                                                          value: `${SHARED_PREFIX}${shared.internalReportSharedParameterId}`,
                                                          label: shared.name,
                                                      })),
                                                  },
                                              ]
                                            : []),
                                    ];
                                    const findTypeOption = (value) => {
                                        for (const option of typeOptions) {
                                            if (option.options) {
                                                const found = option.options.find((o) => o.value === value);
                                                if (found) return found;
                                            } else if (option.value === value) {
                                                return option;
                                            }
                                        }
                                        return null;
                                    };

                                    return (
                                <Table responsive size="sm" className="mb-2">
                                    <thead>
                                        <tr>
                                            <th>Type</th>
                                            <th>Display Name</th>
                                            <th>Internal Name</th>
                                            <th>Data Type</th>
                                            <th>Required</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {form.parameters.map((parameter, index) => {
                                            const isCustom = parameter.parameterType === 'Custom';
                                            const isShared = parameter.parameterType === 'Shared';
                                            return (
                                                <tr key={index}>
                                                    <td style={{ minWidth: '160px' }}>
                                                        <Select
                                                            styles={compactSelectStyles}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                            value={findTypeOption(toTypeValue(parameter))}
                                                            onChange={(option) =>
                                                                handleParameterChange(
                                                                    index,
                                                                    'parameterType',
                                                                    option.value
                                                                )
                                                            }
                                                            options={typeOptions}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Input
                                                            bsSize="sm"
                                                            maxLength={100}
                                                            disabled={!isCustom}
                                                            placeholder={
                                                                isCustom ? 'Date From' : parameter.parameterType
                                                            }
                                                            value={isCustom || isShared ? parameter.displayName : ''}
                                                            onChange={(e) =>
                                                                handleParameterChange(
                                                                    index,
                                                                    'displayName',
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                    <td>
                                                        <Input
                                                            bsSize="sm"
                                                            maxLength={100}
                                                            placeholder="date_from"
                                                            value={parameter.internalName}
                                                            onChange={(e) =>
                                                                handleParameterChange(
                                                                    index,
                                                                    'internalName',
                                                                    e.target.value
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                    <td style={{ minWidth: '140px' }}>
                                                        {(() => {
                                                            const dataTypeOptions = (isShared
                                                                ? DATA_TYPES
                                                                : CUSTOM_DATA_TYPES
                                                            ).map((type) => ({ value: type, label: type }));
                                                            return (
                                                                <Select
                                                                    styles={compactSelectStyles}
                                                                    isDisabled={!isCustom}
                                                                    placeholder="-"
                                                                    menuPortalTarget={document.body}
                                                                    menuPosition="fixed"
                                                                    value={
                                                                        isCustom || isShared
                                                                            ? dataTypeOptions.find(
                                                                                  (o) => o.value === parameter.dataType
                                                                              ) || null
                                                                            : null
                                                                    }
                                                                    onChange={(option) =>
                                                                        handleParameterChange(
                                                                            index,
                                                                            'dataType',
                                                                            option ? option.value : ''
                                                                        )
                                                                    }
                                                                    options={dataTypeOptions}
                                                                />
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="text-center align-middle">
                                                        <input
                                                            type="checkbox"
                                                            checked={parameter.isRequired}
                                                            onChange={(e) =>
                                                                handleParameterChange(
                                                                    index,
                                                                    'isRequired',
                                                                    e.target.checked
                                                                )
                                                            }
                                                        />
                                                    </td>
                                                    <td className="text-right">
                                                        <Button
                                                            color="link"
                                                            size="sm"
                                                            className="text-danger p-0"
                                                            title="Remove parameter"
                                                            style={{ cursor: 'pointer' }}
                                                            onClick={() => removeParameter(index)}
                                                        >
                                                            <MdDeleteOutline style={{ fontSize: '18px' }} />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                                    );
                                })()}
                                <Button color="secondary" outline size="sm" className="mb-3" onClick={addParameter}>
                                    Add Parameter
                                </Button>

                                <div className="d-flex justify-content-end align-items-center" style={{ gap: '12px' }}>
                                    {formError && <span className="text-danger small mr-auto">{formError}</span>}
                                    <Button
                                        color="primary"
                                        size="sm"
                                        disabled={isSaving || isUnchanged || Boolean(formError)}
                                        onClick={handleSave}
                                        className="d-flex align-items-center"
                                        style={{ gap: '6px' }}
                                    >
                                        {isSaving && <ButtonLoader />}
                                        {isSaving
                                            ? isEditing
                                                ? 'Updating...'
                                                : 'Saving...'
                                            : isEditing
                                              ? 'Update'
                                              : 'Save'}
                                    </Button>
                                    <Button color="secondary" outline size="sm" disabled={isSaving} onClick={closeForm}>
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}

                        {reports.length > 0 ? (
                            <div className="px-3">
                            <Table responsive size="sm" className="pharmacy-reports-table">
                                <thead>
                                    <tr>
                                        <th>Report Name</th>
                                        <th>Permission</th>
                                        <th>Parameters</th>
                                        <th>Description</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reports.map((report) => (
                                        <tr key={report.internalReportId}>
                                            <td>{report.reportName}</td>
                                            <td>{toPermissionNameArray(report.permissionName).join(', ') || 'All users'}</td>
                                            <td>
                                                {(report.parameters || [])
                                                    .map((parameter) => parameter.displayName)
                                                    .join(', ') || '-'}
                                            </td>
                                            <td>{report.description}</td>
                                            <td className="text-right text-nowrap">
                                                {canEdit && (
                                                    <Button
                                                        color="link"
                                                        size="sm"
                                                        className="p-0 mr-3"
                                                        title="Edit report"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => openEditForm(report)}
                                                    >
                                                        <MdEdit style={{ fontSize: '18px' }} />
                                                    </Button>
                                                )}
                                                {canDelete && (
                                                    <Button
                                                        color="link"
                                                        size="sm"
                                                        className="text-danger p-0"
                                                        title="Delete report"
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleDelete(report)}
                                                    >
                                                        <MdDeleteOutline style={{ fontSize: '18px' }} />
                                                    </Button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            </div>
                        ) : (
                            !isFormOpen && <div className="text-muted text-center py-4">No reports configured yet.</div>
                        )}
                    </>
                )}
                <SharedParametersModal
                    isOpen={isSharedParametersOpen}
                    onClose={() => setIsSharedParametersOpen(false)}
                    onSharedParametersChanged={handleSharedParametersChanged}
                />
            </ModalBody>
        </Modal>
    );
}

export default ReportSetupModal;
